import { useMemo, useState } from 'react';
import {
  Loader2,
  Plus,
  Trash2,
  Check,
  X,
  FolderTree,
  Tag as TagIcon,
  GripVertical,
  HelpCircle,
} from 'lucide-react';
import { useTags, useCreateTag, useUpdateTag, useDeleteTag, type Tag } from '../../hooks/useTags';

const DEFAULT_COLOR = '#7e4391';
const NONE = '';

/**
 * Gestione TAG + COLLEZIONI.
 * - Le "collezioni" sono tag di primo livello (parent_id = null) usati come contenitore.
 * - Crea/rinomina/colora tag e assegnali a una collezione (select o DRAG & DROP).
 * - Trascina un tag su una collezione per spostarlo; trascinalo sulla zona "Primo livello"
 *   per toglierlo dalla collezione. Popup "?" con le istruzioni.
 */
export function TagManager() {
  const { data: tags, isLoading } = useTags();
  const createTag = useCreateTag();
  const updateTag = useUpdateTag();
  const deleteTag = useDeleteTag();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<{ name: string; color: string; icon: string; parent: string }>({
    name: '',
    color: DEFAULT_COLOR,
    icon: '',
    parent: NONE,
  });
  const [showHelp, setShowHelp] = useState(false);
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | 'root' | null>(null);

  // Collezioni = tag senza parent (di primo livello).
  const collections = useMemo(() => (tags ?? []).filter((t) => !t.parent_id), [tags]);
  // Mappa collezione -> figli.
  const childrenOf = useMemo(() => {
    const m = new Map<string, Tag[]>();
    for (const t of tags ?? []) {
      if (t.parent_id) {
        const arr = m.get(t.parent_id) ?? [];
        arr.push(t);
        m.set(t.parent_id, arr);
      }
    }
    return m;
  }, [tags]);

  const hasChildren = (id: string) => (childrenOf.get(id)?.length ?? 0) > 0;

  function startCreate() {
    setEditingId(null);
    setCreating(true);
    setDraft({ name: '', color: DEFAULT_COLOR, icon: '', parent: NONE });
  }
  function startEdit(t: Tag) {
    setCreating(false);
    setEditingId(t.id);
    setDraft({ name: t.name, color: t.color, icon: t.icon ?? '', parent: t.parent_id ?? NONE });
  }
  function reset() {
    setEditingId(null);
    setCreating(false);
  }
  async function save() {
    const name = draft.name.trim();
    if (!name) return;
    const body = {
      name,
      color: draft.color,
      icon: draft.icon.trim() || null,
      parent_id: draft.parent || null,
    };
    if (creating) await createTag.mutateAsync(body);
    else if (editingId) await updateTag.mutateAsync({ id: editingId, body });
    reset();
  }

  // Sposta un tag in una collezione (parentId) o al primo livello (null).
  function reparent(tagId: string, parentId: string | null) {
    if (tagId === parentId) return;
    // Evita nidificazione a 2 livelli: una collezione (tag con figli) non entra in un'altra.
    if (parentId && hasChildren(tagId)) return;
    const cur = (tags ?? []).find((t) => t.id === tagId);
    if (cur && (cur.parent_id ?? null) === parentId) return;
    updateTag.mutate({ id: tagId, body: { parent_id: parentId } });
  }

  function onDrop(target: string | null) {
    if (dragId) reparent(dragId, target);
    setDragId(null);
    setOverId(null);
  }

  const busy = createTag.isPending || updateTag.isPending;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div>
            <h3 className="text-sm font-semibold text-zinc-100">Tag e collezioni</h3>
            <p className="text-[11px] text-zinc-500">
              Trascina un tag su una collezione per spostarlo. Raccogli i tag in collezioni.
            </p>
          </div>
          <div className="relative">
            <button
              onClick={() => setShowHelp((v) => !v)}
              className="rounded-full p-1 text-zinc-400 transition hover:bg-zinc-800 hover:text-zinc-200"
              title="Come si usa"
            >
              <HelpCircle size={15} />
            </button>
            {showHelp && <HelpPopup onClose={() => setShowHelp(false)} />}
          </div>
        </div>
        <button
          onClick={startCreate}
          className="flex items-center gap-1.5 rounded-md bg-accent-500 px-2.5 py-1.5 text-xs font-medium text-white transition hover:bg-accent-600"
        >
          <Plus size={13} /> Nuovo tag
        </button>
      </div>

      {(creating || editingId) && (
        <TagEditor
          draft={draft}
          setDraft={setDraft}
          collections={collections}
          editingId={editingId}
          onSave={save}
          onCancel={reset}
          busy={busy}
        />
      )}

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 size={16} className="animate-spin text-zinc-600" />
        </div>
      ) : !tags || tags.length === 0 ? (
        <p className="py-8 text-center text-xs text-zinc-500">Nessun tag definito.</p>
      ) : (
        <div className="space-y-2">
          {/* Zona "Primo livello": togli un tag dalla collezione (visibile durante il trascinamento) */}
          {dragId && (
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setOverId('root');
              }}
              onDragLeave={() => setOverId((o) => (o === 'root' ? null : o))}
              onDrop={() => onDrop(null)}
              className={
                'rounded-md border border-dashed px-3 py-2 text-center text-[11px] transition ' +
                (overId === 'root'
                  ? 'border-accent-500 bg-accent-500/10 text-accent-300'
                  : 'border-zinc-700 text-zinc-500')
              }
            >
              Rilascia qui per togliere dalla collezione (primo livello)
            </div>
          )}

          {collections.map((col) => {
            const kids = childrenOf.get(col.id) ?? [];
            const isDropTarget = overId === col.id;
            return (
              <div
                key={col.id}
                onDragOver={(e) => {
                  e.preventDefault();
                  setOverId(col.id);
                }}
                onDragLeave={() => setOverId((o) => (o === col.id ? null : o))}
                onDrop={() => onDrop(col.id)}
                className={
                  'rounded-md border transition ' +
                  (isDropTarget ? 'border-accent-500 ring-1 ring-accent-500/40' : 'border-zinc-800')
                }
              >
                <TagRow
                  tag={col}
                  isCollection
                  dragging={dragId === col.id}
                  onDragStart={() => setDragId(col.id)}
                  onDragEnd={() => {
                    setDragId(null);
                    setOverId(null);
                  }}
                  onEdit={() => startEdit(col)}
                  onDelete={() => deleteTag.mutate(col.id)}
                />
                {kids.length > 0 && (
                  <ul className="divide-y divide-zinc-800/70 border-t border-zinc-800/70 pl-5">
                    {kids.map((t) => (
                      <TagRow
                        key={t.id}
                        tag={t}
                        dragging={dragId === t.id}
                        onDragStart={() => setDragId(t.id)}
                        onDragEnd={() => {
                          setDragId(null);
                          setOverId(null);
                        }}
                        onEdit={() => startEdit(t)}
                        onDelete={() => deleteTag.mutate(t.id)}
                      />
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TagRow({
  tag,
  isCollection,
  dragging,
  onDragStart,
  onDragEnd,
  onEdit,
  onDelete,
}: {
  tag: Tag;
  isCollection?: boolean;
  dragging?: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', tag.id);
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      className={'flex items-center justify-between px-3 py-2 ' + (dragging ? 'opacity-40' : '')}
    >
      <div className="flex items-center gap-2">
        <GripVertical size={13} className="shrink-0 cursor-grab text-zinc-600" />
        <button onClick={onEdit} className="flex items-center gap-2 text-left">
          {isCollection ? (
            <FolderTree size={13} className="shrink-0 text-zinc-500" />
          ) : (
            <TagIcon size={12} className="shrink-0 text-zinc-600" />
          )}
          <span className="h-3 w-3 shrink-0 rounded-full ring-1 ring-white/10" style={{ backgroundColor: tag.color }} />
          <span className={'text-sm ' + (isCollection ? 'font-semibold text-zinc-100' : 'text-zinc-200')}>
            {tag.name}
          </span>
          {tag.is_ai_managed && (
            <span className="rounded bg-zinc-800 px-1 text-[9px] uppercase tracking-wider text-zinc-400">AI</span>
          )}
        </button>
      </div>
      <button
        onClick={onDelete}
        className="rounded p-1 text-zinc-500 transition hover:bg-rose-500/10 hover:text-rose-400"
        title="Elimina"
      >
        <Trash2 size={13} />
      </button>
    </div>
  );
}

function HelpPopup({ onClose }: { onClose: () => void }) {
  return (
    <>
      {/* click-away */}
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div className="absolute left-0 top-7 z-50 w-72 rounded-lg border border-zinc-700 bg-zinc-900 p-3 text-xs text-zinc-300 shadow-xl">
        <div className="mb-1.5 flex items-center justify-between">
          <span className="font-semibold text-zinc-100">Come gestire tag e collezioni</span>
          <button onClick={onClose} className="rounded p-0.5 text-zinc-500 hover:text-zinc-300">
            <X size={13} />
          </button>
        </div>
        <ul className="space-y-1.5 leading-relaxed">
          <li>• <b>Nuovo tag</b>: crea un'etichetta con nome, colore ed eventuale collezione.</li>
          <li>• <b>Collezione</b>: un tag di primo livello che raccoglie altri tag.</li>
          <li>• <b>Sposta</b>: trascina un tag su una collezione per spostarlo lì.</li>
          <li>• <b>Togli dalla collezione</b>: trascina il tag sulla zona “Primo livello”.</li>
          <li>• <b>Modifica</b>: clic sul nome del tag. <b>Elimina</b>: icona cestino.</li>
        </ul>
      </div>
    </>
  );
}

function TagEditor({
  draft,
  setDraft,
  collections,
  editingId,
  onSave,
  onCancel,
  busy,
}: {
  draft: { name: string; color: string; icon: string; parent: string };
  setDraft: (d: { name: string; color: string; icon: string; parent: string }) => void;
  collections: Tag[];
  editingId: string | null;
  onSave: () => void;
  onCancel: () => void;
  busy: boolean;
}) {
  const parentOptions = collections.filter((c) => c.id !== editingId);
  return (
    <div className="flex flex-wrap items-end gap-3 rounded-md border border-zinc-800 bg-zinc-950/40 p-3">
      <div className="min-w-[140px] flex-1">
        <label className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-zinc-500">Nome</label>
        <input
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          placeholder="Es. Fatture"
          className="w-full rounded-md border border-zinc-800 bg-zinc-950/60 px-2.5 py-1.5 text-sm outline-none focus:border-accent-500/50"
        />
      </div>
      <div>
        <label className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-zinc-500">Colore</label>
        <div className="flex items-center gap-1.5">
          <input
            type="color"
            value={draft.color}
            onChange={(e) => setDraft({ ...draft, color: e.target.value })}
            className="h-8 w-9 cursor-pointer rounded border border-zinc-800 bg-transparent"
          />
          <input
            value={draft.color}
            onChange={(e) => setDraft({ ...draft, color: e.target.value })}
            className="w-20 rounded-md border border-zinc-800 bg-zinc-950/60 px-2 py-1.5 font-mono text-xs outline-none focus:border-accent-500/50"
          />
        </div>
      </div>
      <div className="min-w-[150px]">
        <label className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-zinc-500">Collezione</label>
        <select
          value={draft.parent}
          onChange={(e) => setDraft({ ...draft, parent: e.target.value })}
          className="w-full rounded-md border border-zinc-800 bg-zinc-950/60 px-2.5 py-1.5 text-sm outline-none focus:border-accent-500/50"
        >
          <option value="">— Nessuna —</option>
          {parentOptions.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>
      <div className="flex items-center gap-1.5">
        <button
          onClick={onSave}
          disabled={busy || !draft.name.trim()}
          className="flex items-center gap-1 rounded-md bg-accent-500 px-2.5 py-1.5 text-xs font-medium text-white transition hover:bg-accent-600 disabled:opacity-40"
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
          Salva
        </button>
        <button onClick={onCancel} className="rounded-md border border-zinc-700 p-1.5 text-zinc-400 transition hover:bg-zinc-800">
          <X size={13} />
        </button>
      </div>
    </div>
  );
}
